import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { PlatformLocation } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  Renderer2,
  ViewChild,
  ViewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { fromEvent, Observable, Subject } from 'rxjs';
import SwiperCore, { Autoplay, Pagination, SwiperOptions } from 'swiper';
import { SwiperComponent } from 'swiper/angular';

import { environment } from '@fantaz/shared/app-config';
import { AuthFacade } from '@fantaz/shared/auth';
import {
  CasualTournamentEntity,
  CurrentUser,
} from '@fantaz/shared/data-access/models';
import { ShareService } from '@fantaz/shared/share';
import { LoggerService } from '@fantaz/shared/utils/log';
import { RouterUtil } from '@fantaz/shared/utils/router';
import { BannerComponent } from '@fantaz/tournaments-v2/ui/tournament-banner';
import {
  getAllAdvertisement,
  loadAdvertisement,
} from '@fantaz/tournaments/data-access';

SwiperCore.use([Pagination, Autoplay]);

@UntilDestroy()
@Component({
  selector: 'fz-widget-tournament-slider',
  templateUrl: './tournament-slider.component.html',
  styleUrls: ['./tournament-slider.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TournamentSliderComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @ViewChild('tournament_slider', { static: true })
  tournamentSliderEl: ElementRef;

  @ViewChildren(BannerComponent, { read: ElementRef })
  bannerElements: QueryList<ElementRef>;

  @Input()
  sticky = false;

  @Input()
  ratio = 2;

  @Input()
  startPosFix = 10;

  @Input()
  minHeight = 96;

  initialTop = 116;
  initialHeight = 0;

  firstSliderEl: ElementRef<HTMLDivElement>;
  banners: ElementRef[] = [];

  currentUser$ = this.authFacade.currentUser$;
  currentUser: CurrentUser | null | undefined;
  advertisements$: Observable<Array<CasualTournamentEntity>> =
    this.store.select(getAllAdvertisement);
  advertisements: Array<CasualTournamentEntity> = [];
  unsubscribe$: Subject<void> = new Subject();

  config: SwiperOptions = {
    slidesPerView: 1,
    spaceBetween: 0,
    autoplay: {
      disableOnInteraction: false,
      pauseOnMouseEnter: true,
      delay: 10000,
    },
    loop: true,
    navigation: false,
    pagination: { clickable: true },
    scrollbar: { draggable: true },
  };

  get existVideoInAnyAdvertisement(): boolean {
    if (this.advertisements && this.advertisements.length > 0) {
      return (
        this.advertisements.find(
          (el) =>
            el.metadata &&
            el.metadata['vipVideo'] &&
            el.metadata['vipVideo'] !== null,
        ) !== undefined
      );
    }

    return false;
  }

  @ViewChild('swiper') swiper: SwiperComponent;

  constructor(
    private readonly logger: LoggerService,
    private readonly authFacade: AuthFacade,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private breakpointObserver: BreakpointObserver,
    private store: Store,
    private platformLocation: PlatformLocation,
    private shareService: ShareService,
    private renderer: Renderer2,
    private sliderEl: ElementRef,
  ) {}

  ngOnInit(): void {
    this.store.dispatch(loadAdvertisement());
    this.breakpointObserver
      .observe(['(max-width: 599px)'])
      .subscribe((result: BreakpointState) =>
        this.updateInitialTop(result.matches),
      );
    this.advertisements$
      .pipe(untilDestroyed(this))
      .subscribe((advertisements) => {
        this.advertisements = advertisements;

        if (advertisements.length == 0 && this.swiper?.swiperRef?.autoplay) {
          this.swiper.swiperRef.autoplay.stop();
        }
      });
    this.currentUser$.pipe(untilDestroyed(this)).subscribe((user) => {
      this.currentUser = user;
    });
  }

  ngAfterViewInit() {
    if (this.sticky) {
      this.onWindowScroll();
      this.initialHeight = this.sliderEl.nativeElement.offsetHeight;
      this.firstSliderEl = this.sliderEl.nativeElement.querySelector('#slide');
    }

    this.bannerElements.changes.subscribe((banners) =>
      banners.forEach((banner: ElementRef) => {
        this.banners.push(banner.nativeElement.querySelector('.banner'));
      }),
    );
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  onWindowScroll() {
    fromEvent(window, 'scroll')
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        const scrollY =
          window.pageYOffset || document.documentElement.scrollTop;
        if (scrollY) {
          this.updateTournamentSliderHeight(scrollY);
        }
      });
  }

  updateInitialTop(matches: boolean) {
    const cssStyleDeclaration = getComputedStyle(document.body);
    const toolbarHeight =
      +cssStyleDeclaration
        .getPropertyValue('--toolbar-height')
        .replace('px', '') || 80;
    const toolbarHeightMobile =
      +cssStyleDeclaration
        .getPropertyValue('--toolbar-height-mobile')
        .replace('px', '') || 56;
    const breadcrumbHeight =
      +cssStyleDeclaration
        .getPropertyValue('--breadcrumb-height')
        .replace('px', '') || 36;

    this.initialTop = matches
      ? breadcrumbHeight + toolbarHeightMobile
      : breadcrumbHeight + toolbarHeight;
  }

  adjustSliderHeight(el: ElementRef, height: string) {
    this.renderer.setStyle(el, 'height', height);
  }

  updateTournamentSliderHeight(scrollY: number) {
    const isSticky = scrollY > this.startPosFix;
    const val = Math.ceil(this.initialHeight - scrollY * this.ratio);
    const height = val > this.minHeight ? val + 'px' : this.minHeight + 'px';

    if (isSticky) {
      this.renderer.addClass(this.sliderEl.nativeElement, 'sticky');
      this.renderer.addClass(this.tournamentSliderEl.nativeElement, 'sticky');
      this.renderer.setStyle(this.sliderEl.nativeElement, 'position', 'sticky');
      this.renderer.setStyle(this.sliderEl.nativeElement, 'z-index', '11');

      this.banners.forEach((banner) =>
        this.renderer.addClass(banner, 'banner-sticky'),
      );

      this.adjustSliderHeight(this.tournamentSliderEl.nativeElement, height);
      this.firstSliderEl && this.adjustSliderHeight(this.firstSliderEl, height);
      this.banners.forEach((banner) => this.adjustSliderHeight(banner, height));
    } else {
      this.renderer.removeClass(this.sliderEl.nativeElement, 'sticky');
      this.renderer.removeClass(
        this.tournamentSliderEl.nativeElement,
        'sticky',
      );

      this.banners.forEach((banner) => {
        this.renderer.removeClass(banner, 'banner-sticky');
      });

      this.adjustSliderHeight(this.tournamentSliderEl.nativeElement, '');
      this.firstSliderEl && this.adjustSliderHeight(this.firstSliderEl, '');
      this.banners.forEach((banner) => this.adjustSliderHeight(banner, ''));
    }
  }

  onSharePress(tournament: CasualTournamentEntity) {
    this.logger.log('[Casual Tournaments Share Tournament]', tournament);

    const tournamentType = tournament.lifeExperience ? 'prizes' : 'cash';
    let urlStr = `${environment.baseURL}/${RouterUtil.Configuration.Tournaments}/${tournamentType}/${tournament.url}`;

    if (tournament.lifeExperience) {
      urlStr += `/${tournament.eventSchedules[0].uuid}`;

      if (tournament.metadata?.vip === 'true') {
        urlStr = `${window.location.origin}/vip/${tournament.url}`;
      }
    }
    const url = new URL(urlStr);
    this.shareService.share({
      title: 'FantaZ',
      text: 'Play Casual tournaments',
      url: url.toString(),
      currentUser: this.currentUser,
      image: tournament.game.imageUrl,
    });
  }

  onJoinPress(tournament: CasualTournamentEntity) {
    this.logger.log('[Casual Tournaments Card Press]', tournament);
    let route = '';

    if (tournament.lifeExperience) {
      route += `/prizes/${tournament.url}/${tournament.eventSchedules[0].uuid}`;
    } else {
      route += `/cash/${tournament.url}`;
    }

    this.router.navigate([route], {
      relativeTo: this.activatedRoute,
    });
  }
}
